import React, { useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ROUTES from '@src/constants/route';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_CHANGE_TYPES,
  PACKAGE_TYPE,
  VERISON_PACKAGE,
} from '@src/constants/package';
import { EVENT_TYPE } from '@src/constants';
import StackedAvatars from '@src/components/StackedAvatars';
import { capitalize } from 'lodash';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { checkVietNam } from '@src/utils/checkCountry';
import { COLOR } from '@src/styles/color';
import {
  getFakeEndTimeDiscountForPackage,
  getPackageChangeType,
  getCustomPackagePrice,
  roundSaleOffPercentage,
  checkCustomPackage,
} from '@src/services/package';
import avatarMP11 from '@src/assets/avatars/avatar-mp-1-1.png';
import avatarMP12 from '@src/assets/avatars/avatar-mp-1-2.png';
import avatarMP13 from '@src/assets/avatars/avatar-mp-1-3.png';
import avatarMP21 from '@src/assets/avatars/avatar-mp-2-1.png';
import avatarMP22 from '@src/assets/avatars/avatar-mp-2-2.png';
import avatarMP23 from '@src/assets/avatars/avatar-mp-2-3.png';
import avatarMP31 from '@src/assets/avatars/avatar-mp-3-1.png';
import avatarMP32 from '@src/assets/avatars/avatar-mp-3-2.png';
import avatarMP33 from '@src/assets/avatars/avatar-mp-3-3.png';
import useFeatureFlags from '@src/hooks/useFeatureFlags';

import { getPriceFromVoucher, getSavedPrice } from '@src/services/voucher';
import NewCountdownBanner from '../NewCountdownBanner';
import { StyledPackageItemWrapper } from '../index.style';
import PackageHeader from './components/PackageHeader';
import PackagePrice from './components/PackagePrice';
import CustomResources from '../CustomResources';
import PackageFeature from './components/PackageFeature';
import { StyledPackageCustomItem } from './index.style';

const CustomPackage = ({
  packages,
  pkg,
  isPushEventViewPricing,
  isMobile,
  hasDiscountPackage,
  totalVoucher,
  firstPaidVoucher,
  isRow,
  duration,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const location = useLocation();
  const { voucherCode, bannerType } = camelcaseKeys(
    queryString.parse(location.search) || {},
  );
  const { user = {} } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);
  const [optionsSelected, setOptionsSelected] = useState({
    credits: 0,
    devices: 0,
    voiceClonings: 0,
  });
  const { getFeatureValue } = useFeatureFlags();

  const isPackageCustom = checkCustomPackage({
    level: pkg.level,
    type: pkg.type,
  });
  const isVietNam = checkVietNam();
  const { originalPrice, price } = getCustomPackagePrice({
    credits: optionsSelected.credits,
    voiceClonings: optionsSelected.voiceClonings,
    devices: optionsSelected.devices,
    customResource: pkg?.customResource,
    isVietNam,
  });
  const getBestVoucher = () => {
    const canUseVouchers = totalVoucher;
    const listVoucherSortByRanking = canUseVouchers
      .map((voucherItem) => {
        const reducedPrice = getSavedPrice({
          voucher: voucherItem,
          currPrice: hasDiscountPackage
            ? pkg?.discountConfig?.price || price
            : price,
          currUsdPrice: hasDiscountPackage
            ? pkg?.discountConfig?.usdPrice || price
            : price,
        });
        return { ...voucherItem, reducedPrice };
      })
      .sort((a, b) => b.reducedPrice - a.reducedPrice);

    const bestVoucher = listVoucherSortByRanking.length
      ? listVoucherSortByRanking[0]
      : null;

    return bestVoucher;
  };

  const bestVoucher = getBestVoucher();

  const priceWithSaleOff = getPriceFromVoucher({
    isVietNam,
    voucher: bestVoucher,
    currPrice: price,
    currUsdPrice: price,
  });

  const isDiscountUsingVoucher = bestVoucher?.value || originalPrice !== price;
  const isApplyFirstPaidVoucher =
    (bestVoucher?.description || 0) === (firstPaidVoucher?.description || 1);

  const sendEventAddToCartMoEngage = (packageCode) => {
    const [product, packageItem, period] = pkg.code.split('-');
    const addToCartEventData = {
      event: EVENT_TYPE.ADD_TO_CART,
      userId: user?.id,
      email: user.email,
      package_code: packageCode,
      product,
      plan: packageItem,
      period,
      voucher_type: bestVoucher?.type,
      voucher_code: bestVoucher?.code,
      voucher_amount: bestVoucher?.value,
      package_price: pkg.price,
      isTopUp: false,
      min_credit_topup: undefined,
      min_price_topup: undefined,
    };
    window.dataLayer.push(addToCartEventData);
  };

  const handleBuyPackage = async () => {
    const packageCode = pkg?.code;

    if (isPushEventViewPricing) {
      sendEventAddToCartMoEngage(packageCode);
    }
    const { credits, devices, voiceClonings } = optionsSelected || {};
    const basePaymentParams = {
      package: packageCode,
      credits,
      devices,
      voiceClonings,
    };

    const hasVoucher = Boolean(voucherCode);
    const voucherPaymentParams = hasVoucher ? { voucherCode, bannerType } : {};

    const paymentParams = { ...basePaymentParams, ...voucherPaymentParams };
    const queryParams = queryString.stringify(paymentParams);

    history.push(`${ROUTES.PAYMENT}?${queryParams}`);
  };

  const getPreviousPackageInfo = () => {
    const apiUsingPackage = packages?.find(
      (pack) => pack?.code === user?.apiUsageOptions?.code,
    );
    const prevPackage = apiUsingPackage || usingPackage;
    const prevPackageExpiredDate = apiUsingPackage
      ? user?.apiPackage?.packageExpiryDate
      : user?.packageExpiryDate;
    return { prevPackage, prevPackageExpiredDate, apiUsingPackage };
  };

  const renderButtonTextV3 = () => {
    const { prevPackage, prevPackageExpiredDate, apiUsingPackage } =
      getPreviousPackageInfo();

    const determinePackageChange = () => {
      if (!usingPackage) return null;
      return getPackageChangeType({
        currPackage: pkg,
        prevPackage,
        prevPackageExpiredDate,
      });
    };
    const packageChangeType = determinePackageChange();
    const isUpgradePackage = packageChangeType === PACKAGE_CHANGE_TYPES.UPGRADE;

    const versionInUseInValid = apiUsingPackage
      ? apiUsingPackage?.version !== VERISON_PACKAGE.V2_1
      : usingPackage?.version !== VERISON_PACKAGE.V3;

    const isFreePkg = FREE_PACKAGE_CODES.includes(pkg.code);
    const isUsingFreePkg = FREE_PACKAGE_CODES.includes(usingPackage?.code);

    if (isFreePkg) return isUsingFreePkg ? t('using') : t('activeNow');

    const isSamePackageOrInvalid =
      pkg?.code === usingPackage?.code || versionInUseInValid;
    const discountPercentage =
      ((originalPrice - priceWithSaleOff) / originalPrice) * 100;
    const saleOffPercentage = roundSaleOffPercentage(discountPercentage);

    const getBuyNowLabel = () =>
      saleOffPercentage
        ? t('buyNowWithSaleOff', { percent: saleOffPercentage })
        : t('buyNow');

    const getUpgradeLabel = () =>
      saleOffPercentage
        ? t('upgradeNowWithSaleOFF', { percent: saleOffPercentage })
        : t('upgradeNow');
    if (isSamePackageOrInvalid) return getBuyNowLabel();
    if (isUpgradePackage) return getUpgradeLabel();
    return getBuyNowLabel();
  };

  const onPriceChange = (key, customOptionValue) => {
    setOptionsSelected((prev) => ({ ...prev, [key]: customOptionValue }));
  };
  const onInitializePrices = (initialPrice) => {
    setOptionsSelected(initialPrice);
  };

  const MostPopularBanner = () => (
    <div className="most-popular-banner">
      <Typography className="most-popular-text">
        {capitalize(t('mostPopular'))}
      </Typography>
    </div>
  );
  const EmptyBanner = () => <div />;

  const renderBannerPackage = (discount = {}, mostPopular) => {
    const { startAt, endAt } = discount;
    const inDiscountTime = startAt && moment().isBetween(startAt, endAt);
    let endAtToShow = endAt;
    const useFakeEndAt = getFeatureValue(FEATURE_KEYS.FAKE_END_TIME_VOUCHER); // hours
    if (useFakeEndAt) {
      endAtToShow = getFakeEndTimeDiscountForPackage({
        startTimeToCountdown: startAt,
        timeToResetCountdown: useFakeEndAt,
        endTimeToCountdown: endAt,
      });
    }
    if (inDiscountTime) {
      return (
        <NewCountdownBanner
          endAt={!useFakeEndAt ? pkg.discountConfig?.endAt : endAtToShow || {}}
        />
      );
    }
    if (mostPopular) return <MostPopularBanner />;
    return <EmptyBanner />;
  };

  const renderRandomAvatars = () => {
    const avatarsDay1List = [avatarMP11, avatarMP12, avatarMP13];
    const avatarsDay2List = [avatarMP21, avatarMP22, avatarMP23];
    const avatarsDay3List = [avatarMP31, avatarMP32, avatarMP33];
    const allAvatars = [avatarsDay1List, avatarsDay2List, avatarsDay3List];
    const currentDate = new Date();
    const dayOfWeek = currentDate.getDay();
    return allAvatars[dayOfWeek % allAvatars.length];
  };

  const ButtonBuyPackageView = () => (
    <Button
      fullWidth
      className={classNames('button-start', {
        'button-highlight': pkg.mostPopular,
      })}
      variant={pkg.mostPopular ? 'contained' : 'outlined'}
      data-id="add-to-cart"
      data-product={pkg?.type}
      data-package-code={pkg?.code}
      data-pricing-duration={duration}
      data-button-text={renderButtonTextV3()}
      onClick={() => handleBuyPackage(null)}
    >
      {renderButtonTextV3()}
    </Button>
  );

  const renderAvatarMostPopular = () => (
    <div className="popularity">
      <StackedAvatars images={renderRandomAvatars(false)} />
      <Typography
        className="popularity-text"
        variant="body2"
        sx={{ color: COLOR.black[64] }}
      >
        {t('mostPeopleChooseThis', { percent: pkg?.popularPercent || 86 })}
      </Typography>
    </div>
  );

  const renderAvatarOrPlaceholder = () => {
    if (pkg.mostPopular) {
      return renderAvatarMostPopular();
    }
    if (isMobile) {
      return null;
    }
    return <div style={{ height: '24px' }} />;
  };

  return (
    <StyledPackageItemWrapper
      isPoppular={pkg.mostPopular}
      isMobile={isMobile}
      isRow={isRow && isPackageCustom}
    >
      {renderBannerPackage(pkg.discountConfig, pkg.mostPopular)}
      <StyledPackageCustomItem
        isPoppular={pkg.mostPopular}
        isRow={isRow}
        isApiPack={pkg?.type === PACKAGE_TYPE.API}
        isMobile={isMobile}
        highlight={pkg.mostPopular}
        isDiscountPackage={
          hasDiscountPackage || (pkg.mostPopular && !hasDiscountPackage)
        }
        isVietNam={isVietNam}
      >
        <div className="card">
          {/* Package Header */}
          <div className="container-package-header">
            <PackageHeader
              pkg={pkg}
              bestVoucher={bestVoucher}
              isApplyFirstPaidVoucher={isApplyFirstPaidVoucher}
              language={language}
              isDiscountPackage={hasDiscountPackage}
            />
            <div className="package-feature-row">
              <PackageFeature isMobile={isMobile} pkg={pkg} />
            </div>
          </div>
          <div className="container-package-price-and-option">
            {/* Package Price */}
            <div className="container-package-price">
              <PackagePrice
                pkg={pkg}
                isVietNam={isVietNam}
                flags={{ isVietNam, isMobile }}
                discounts={{
                  shouldShowDiscount:
                    hasDiscountPackage || isDiscountUsingVoucher,
                }}
                pricing={{ originalPrice, price: priceWithSaleOff }}
              />

              {/* Card Content: Button, Resources & Features */}
              <div className="card-content">
                <ButtonBuyPackageView />
                {renderAvatarOrPlaceholder()}
                {!pkg?.mostPopular && <div className="box-range" />}
              </div>
            </div>
            <div className="container-resource">
              <CustomResources
                pkg={pkg}
                onPriceChange={onPriceChange}
                onInitializePrices={onInitializePrices}
              />
            </div>
          </div>
          <div className="package-feature-column">
            <PackageFeature isMobile={isMobile} pkg={pkg} />
          </div>
        </div>
      </StyledPackageCustomItem>
    </StyledPackageItemWrapper>
  );
};

export default CustomPackage;
